import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Edit2 from 'lucide-react/dist/esm/icons/pen.js';
import Check from 'lucide-react/dist/esm/icons/check.js';
import X from 'lucide-react/dist/esm/icons/x.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import Image from 'lucide-react/dist/esm/icons/image.js';
import FileText from 'lucide-react/dist/esm/icons/file-text.js';
import Loader2 from 'lucide-react/dist/esm/icons/loader-2.js';
import Copy from 'lucide-react/dist/esm/icons/copy.js';
import CopyCheck from 'lucide-react/dist/esm/icons/copy-check.js';
import Info from 'lucide-react/dist/esm/icons/info.js';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslations } from '../contexts/TranslationContext';
import { useEditMode } from '../contexts/EditModeContext';
import { useOptionalContent } from '../contexts/ContentContext';
import { SiteConfigContext } from '../contexts/SiteConfigContext';
import { CharityThemeContext } from '../contexts/CharityThemeContext';
import ImagePickerModal from './ImagePickerModal';
import { buildSrcSet, hasResponsiveVariants } from '../utils/imageUtils';
import ContentPickerModal from './ContentPickerModal';
import RichContentRenderer from './RichContentRenderer';
import { createContentReferenceToken } from '../utils/contentReferences';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import api, { apiRoutes } from '../api';
import Maximize2 from 'lucide-react/dist/esm/icons/maximize-2.js';
import Minimize2 from 'lucide-react/dist/esm/icons/minimize-2.js';
import Circle from 'lucide-react/dist/esm/icons/circle.js';

const BLOCK_HTML_PATTERN = /<\/?(?:address|article|aside|blockquote|div|figure|figcaption|footer|h[1-6]|header|hr|li|main|nav|ol|p|pre|section|table|tbody|td|tfoot|th|thead|tr|ul)\b/i;

// Replaces {{token}} placeholders with live site values.
const interpolateTokens = (text, tokens) => {
  if (!text || !tokens || typeof text !== 'string') return text;
  if (!text.includes('{{')) return text;
  return text.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (match, name) =>
    tokens[name] !== undefined && tokens[name] !== null ? String(tokens[name]) : match
  );
};

export default function InlineTranslationEdit({
  translationKey,
  contentKey,
  value,
  displayValue,
  fallback,
  defaultText,
  children,
  onUpdate,
  editMode: editModeProp = false,
  multiline = false,
  className = ''
}) {
  // Support alias props used by ContentContext-based pages
  const resolvedKey = translationKey || contentKey;
  const childText = (typeof children === 'string' || typeof children === 'number') ? String(children) : null;
  const rawValue = typeof value === 'object' && value !== null ? JSON.stringify(value) : value;
  const resolvedValue = rawValue ?? childText;
  const rawFallback = typeof fallback === 'object' && fallback !== null ? JSON.stringify(fallback) : fallback;
  const rawDefaultText = typeof defaultText === 'object' && defaultText !== null ? JSON.stringify(defaultText) : defaultText;
  const resolvedFallback = rawFallback || rawDefaultText;
  const { language, enabledLanguages } = useLanguage();
  const { rawTranslations, t } = useTranslations();
  const { editMode: contextEditMode, handleTranslationUpdate } = useEditMode();
  const contentContext = useOptionalContent();
  // Optional contexts — safe via useContext (returns undefined outside provider)
  const siteConfig = React.useContext(SiteConfigContext);
  const charityTheme = React.useContext(CharityThemeContext);
  const label = (key, fallback) => {
    const translated = t(key);
    return translated === key ? fallback : translated;
  };
  const editMode = editModeProp || contextEditMode;
  const effectiveOnUpdate = onUpdate || handleTranslationUpdate;
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(resolvedValue || resolvedFallback);
  const [otherLangValues, setOtherLangValues] = useState({});
  const [isHtmlMode, setIsHtmlMode] = useState(() => /<[^>]+>/.test(String(resolvedValue || resolvedFallback || '')));
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showContentPicker, setShowContentPicker] = useState(false);
  const [aiLoadingLang, setAiLoadingLang] = useState(null);
  const [aiError, setAiError] = useState('');
  const [aiStyleOpen, setAiStyleOpen] = useState(false);
  const [aiStylePrompt, setAiStylePrompt] = useState('');
  const [aiStyleLoading, setAiStyleLoading] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [tagCopied, setTagCopied] = useState(false);
  const [showTokens, setShowTokens] = useState(false);

  const handleCopyTag = () => {
    navigator.clipboard.writeText(resolvedKey).then(() => {
      setTagCopied(true);
      setTimeout(() => setTagCopied(false), 2000);
    }).catch(() => {});
  };
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const previewRef = useRef(null);
  const savedCursorRef = useRef(null);

  const langName = (code) => t(`admin.languages.${code === 'en' ? 'english' : code === 'fr' ? 'french' : 'spanish'}`);

  // Determine content source:
  //   'organization' — org-specific DB entry (either from rawTranslations/translation system,
  //                    or from ContentContext/OrganizationText passed as the value prop)
  //   'translations' — platform default from the translations DB (t() resolves it)
  //   'fallback'     — hardcoded string, nothing in the DB for this key
  const source = (() => {
    // Check rawTranslations (translation system org overrides)
    if (rawTranslations && Array.isArray(rawTranslations)) {
      const dbEntry = rawTranslations.find(tr => tr.key === resolvedKey && tr.language === language);
      if (dbEntry?.value) return 'organization';
    }
    // Check platform-default translations DB
    const trResult = t(resolvedKey);
    if (trResult !== resolvedKey) return 'translations';
    // If value was explicitly passed as a prop (from ContentContext / OrganizationText table)
    // and is non-empty, it is org-specific content — not a hardcoded fallback.
    if (value !== undefined && value !== null && value !== '') return 'organization';
    return 'fallback';
  })();

  useEffect(() => {
    setEditValue(resolvedValue || resolvedFallback);
  }, [resolvedValue, resolvedFallback]);

  useEffect(() => {
    if (!isEditing || !rawTranslations || !Array.isArray(rawTranslations)) {
      return;
    }

    const otherLanguages = enabledLanguages.filter(lang => lang !== language);
    const values = {};

    otherLanguages.forEach(lang => {
      const translation = rawTranslations.find(
        t => t.key === resolvedKey && t.language === lang
      );
      values[lang] = translation?.value || '';
    });

    console.log('Loading other lang translations:', {
      translationKey: resolvedKey,
      currentLang: language,
      otherLanguages,
      values,
      allTranslations: rawTranslations.filter(t => t.key === resolvedKey)
    });

    setOtherLangValues(values);
  }, [translationKey, language, rawTranslations, isEditing, enabledLanguages]);

  useEffect(() => {
    if (isEditing) {
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        const element = (multiline || isHtmlMode) ? textareaRef.current : inputRef.current;
        element?.focus();
        element?.select();
      }, 50);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isEditing, multiline]);

  const prepareValueForSave = (nextValue) => (
    isHtmlMode ? sanitizeHtml(nextValue) : nextValue
  );

  const handleSave = () => {
    const primaryValue = prepareValueForSave(editValue);
    console.log('InlineTranslationEdit - handleSave called', {
      translationKey: resolvedKey,
      editValue: primaryValue,
      language,
      otherLangValues
    });

    const updates = [
      { key: resolvedKey, value: primaryValue, language }
    ];

    Object.entries(otherLangValues).forEach(([lang, value]) => {
      if (value.trim()) {
        updates.push({ key: resolvedKey, value: prepareValueForSave(value), language: lang });
      }
    });

    console.log('Calling onUpdate for each update:', updates);
    updates.forEach(update => {
      console.log('Updating:', update);
      effectiveOnUpdate(update.key, update.value, update.language);
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(resolvedValue || resolvedFallback);
    setIsEditing(false);
    setIsHtmlMode(false);
    setAiStyleOpen(false);
    setAiStylePrompt('');
    setAiError('');
  };

  // Token chip insertion — works for both <input> and <textarea>
  const insertToken = (token) => {
    const el = isHtmlMode ? textareaRef.current : (inputRef.current || textareaRef.current);
    if (!el) { setEditValue(v => v + token); return; }
    const start = el.selectionStart ?? editValue.length;
    const end = el.selectionEnd ?? editValue.length;
    const newText = editValue.substring(0, start) + token + editValue.substring(end);
    setEditValue(newText);
    setTimeout(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertHtmlSnippet = ({ open, close = '', placeholder = '' }) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = editValue;
    const selectedText = text.substring(start, end);
    const content = selectedText || placeholder;

    const newText = text.substring(0, start) + open + content + close + text.substring(end);

    setEditValue(newText);

    setTimeout(() => {
      textarea.focus();
      const cursorStart = start + open.length;
      const cursorEnd = cursorStart + content.length;
      if (!selectedText && placeholder) {
        textarea.setSelectionRange(cursorStart, cursorEnd);
      } else {
        textarea.setSelectionRange(cursorEnd, cursorEnd);
      }
    }, 0);
  };

  const handleCleanHtml = () => {
    setEditValue(sanitizeHtml(editValue));
  };

  const openImagePicker = () => {
    const textarea = textareaRef.current;
    savedCursorRef.current = textarea ? textarea.selectionStart : editValue.length;
    setShowImagePicker(true);
  };

  const insertAtSavedCursor = (insertedValue) => {
    const pos = savedCursorRef.current ?? editValue.length;
    const newValue = editValue.substring(0, pos) + insertedValue + editValue.substring(pos);
    setEditValue(newValue);
    savedCursorRef.current = null;
    setTimeout(() => {
      const textarea = textareaRef.current;
      if (textarea) {
        textarea.focus();
        const newPos = pos + insertedValue.length;
        textarea.setSelectionRange(newPos, newPos);
      }
    }, 50);
  };

  const handleImageSelect = (imageUrl, imageSizes = []) => {
    let imgHtml;
    // Prefer sizes from the API (authoritative); fall back to URL heuristic for
    // images inserted via the URL tab or recently-used list which have no sizes data.
    const hasSizes = Array.isArray(imageSizes) && imageSizes.length > 0;
    if (hasSizes) {
      const srcset = imageSizes
        .sort((a, b) => a.width - b.width)
        .map(s => `${s.cdn_location} ${s.width}w`)
        .join(', ');
      imgHtml = `<picture><source type="image/webp" srcset="${srcset}" sizes="(max-width: 640px) 100vw, 800px" /><img src="${imageUrl}" alt="" class="max-w-full h-auto rounded my-2" loading="lazy" /></picture>`;
    } else if (hasResponsiveVariants(imageUrl)) {
      const srcset = buildSrcSet(imageUrl);
      imgHtml = `<picture><source type="image/webp" srcset="${srcset}" sizes="(max-width: 640px) 100vw, 800px" /><img src="${imageUrl}" alt="" class="max-w-full h-auto rounded my-2" loading="lazy" /></picture>`;
    } else {
      imgHtml = `<img src="${imageUrl}" alt="" class="max-w-full h-auto rounded my-2" loading="lazy" />`;
    }
    insertAtSavedCursor(imgHtml);
  };

  const openContentPicker = () => {
    const textarea = textareaRef.current;
    savedCursorRef.current = textarea ? textarea.selectionStart : editValue.length;
    setShowContentPicker(true);
  };

  const handleContentSelect = (contentKey) => {
    insertAtSavedCursor(createContentReferenceToken(contentKey));
  };

  const handleStyleAI = () => {
    setAiStylePrompt('');
    setAiError('');
    setAiStyleOpen(true);
  };

  const handleStyleAISubmit = async () => {
    const promptText = aiStylePrompt.trim();
    if (!promptText) {
      setAiError(t('admin.ai.promptRequired') || 'Please enter a prompt.');
      return;
    }
    setAiStyleLoading(true);
    setAiError('');
    try {
      const { data } = await api.post(apiRoutes.AdminContentGenerateHtml, {
        prompt: promptText,
        contextKey: resolvedKey,
        language,
        currentValue: editValue,
      });
      const html = data?.html;
      if (!html) {
        setAiError(data?.errorMessage || t('admin.ai.failed') || 'AI generation failed.');
        return;
      }
      const safe = sanitizeHtml(html);
      setEditValue(safe);
      setIsHtmlMode(true);
      setAiStyleOpen(false);
      setAiStylePrompt('');
      setTimeout(() => textareaRef.current?.focus(), 0);
    } catch (err) {
      const msg = err?.response?.data?.errorMessage
        || err?.message
        || t('admin.ai.failed')
        || 'AI generation failed.';
      setAiError(msg);
    } finally {
      setAiStyleLoading(false);
    }
  };

  const handleGenerateAI = async (targetLang) => {
    const lang = targetLang || language;
    const sourceValue = editValue || resolvedValue || resolvedFallback || '';
    if (!sourceValue) {
      alert(t('admin.ai.sourceRequired') || 'Enter a value in the primary language first.');
      return;
    }
    setAiLoadingLang(lang);
    setAiError('');
    try {
      const resolvedLangName = langName(lang) || lang;
      const isHtml = /<[^>]+>/.test(sourceValue);
      const prompt = isHtml
        ? `Translate the CURRENT VALUE into ${resolvedLangName}. Preserve all HTML tags and structure exactly. Only translate visible text. Do not add or remove tags.`
        : `Translate the CURRENT VALUE into ${resolvedLangName}. Keep the meaning and tone. Return as a single <p> wrapper if no HTML is present, or as plain text wrapped in <span>.`;

      const { data } = await api.post(apiRoutes.AdminContentGenerateHtml, {
        prompt,
        contextKey: resolvedKey,
        language: lang,
        currentValue: sourceValue,
      });
      const html = data?.html;
      if (!html) {
        setAiError(data?.errorMessage || t('admin.ai.failed') || 'AI generation failed.');
        return;
      }
      const safe = sanitizeHtml(html);
      // For non-HTML primary values, strip wrapping <p> / <span> back out for a cleaner inline string.
      let translated = safe;
      if (!isHtml) {
        const stripped = safe
          .replace(/^<(p|span)>/i, '')
          .replace(/<\/(p|span)>$/i, '')
          .trim();
        translated = stripped || safe;
      }
      if (lang === language) {
        setEditValue(translated);
      } else {
        setOtherLangValues((prev) => ({ ...prev, [lang]: translated }));
      }
    } catch (err) {
      const msg = err?.response?.data?.errorMessage
        || err?.message
        || t('admin.ai.failed')
        || 'AI generation failed.';
      setAiError(msg);
    } finally {
      setAiLoadingLang(null);
    }
  };


  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline && !isHtmlMode && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const storedDisplayText = (resolvedValue && resolvedValue !== resolvedKey) ? resolvedValue : (resolvedFallback || resolvedValue);
  const displayText = displayValue ?? storedDisplayText;
  const resolvedDisplayText = (() => {
    const afterRefs = contentContext?.resolveContentValue
      ? contentContext.resolveContentValue(displayText, { currentKey: resolvedKey, editMode })
      : displayText;
    // Build live token map from available contexts
    const nd = siteConfig?.nextDraw;
    const tokens = {
      organizationName: charityTheme?.charityName ?? nd?.charity_name ?? '',
      charityName: charityTheme?.charityName ?? nd?.charity_name ?? '',
      licenseNumber: siteConfig?.siteDetails?.licenseNumber ?? siteConfig?.siteDetails?.license_number ?? '',
      drawDate: nd?.draw_date ? new Date(nd.draw_date).toLocaleDateString() : '',
      closeDate: nd?.cutoff_date ? new Date(nd.cutoff_date).toLocaleDateString() : nd?.close_date ? new Date(nd.close_date).toLocaleDateString() : '',
      jackpot: nd?.current_jackpot != null ? `$${Number(nd.current_jackpot).toLocaleString()}` : '',
      drawTitle: nd?.draw_title ?? nd?.charity_name ?? '',
      ticketPrice: nd?.ticket_price != null ? `$${Number(nd.ticket_price).toLocaleString()}` : '',
    };
    const interpolated = interpolateTokens(afterRefs, tokens);
    return typeof interpolated === 'object' && interpolated !== null ? JSON.stringify(interpolated) : String(interpolated ?? '');
  })();
  const containsHtml = /<[^>]+>/.test(resolvedDisplayText);
  const containsBlockHtml = containsHtml && BLOCK_HTML_PATTERN.test(resolvedDisplayText);
  const previewHtml = contentContext?.resolveContentValue
    ? contentContext.resolveContentValue(editValue, { currentKey: resolvedKey, editMode: true })
    : editValue;

  if (!editMode) {
    if (!displayText && children) return <>{children}</>;
    if (containsHtml) {
      return (
        <RichContentRenderer
          html={resolvedDisplayText}
          className={className}
          inline={!containsBlockHtml}
        />
      );
    }
    if (!className) return <>{resolvedDisplayText}</>;
    return <span className={className}>{resolvedDisplayText}</span>;
  }

  if (!isEditing) {
    const DisplayWrapper = containsBlockHtml ? 'div' : 'span';
    const sourceHoverClasses = source === 'organization'
      ? 'hover:ring-green-500 hover:bg-green-100/50 dark:hover:bg-green-900/50'
      : source === 'translations'
      ? 'hover:ring-blue-500 hover:bg-blue-100/50 dark:hover:bg-blue-900/50'
      : 'hover:ring-amber-500 hover:bg-amber-100/50 dark:hover:bg-amber-900/50';
    const sourceIconColor = source === 'organization'
      ? 'text-green-500'
      : source === 'translations'
      ? 'text-blue-500'
      : 'text-amber-500';
    return (
      <DisplayWrapper
        className={`${className} group/edit relative ${containsBlockHtml ? 'block' : 'inline-flex items-center'} gap-1 cursor-pointer rounded-sm transition-all duration-200 ring-2 ring-transparent ${sourceHoverClasses} px-0.5 -mx-0.5`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsEditing(true);
        }}
        title={t('admin.inlineEdit.clickToEdit')}
      >
        {!displayText && children ? children : (
          containsHtml ? (
            <RichContentRenderer
              html={resolvedDisplayText}
              inline={!containsBlockHtml}
            />
          ) : (
            resolvedDisplayText
          )
        )}
        <Edit2 className={`w-3.5 h-3.5 ${sourceIconColor} opacity-0 group-hover/edit:opacity-100 transition-opacity flex-shrink-0`} />
      </DisplayWrapper>
    );
  }

  const modalContent = (
    <div
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center ${isMaximized ? 'p-0' : 'p-4'}`}
      onClick={handleCancel}
    >
      <div
        className={`${isMaximized ? 'w-full h-full max-w-none' : `${isHtmlMode ? 'w-[1100px]' : 'w-[600px]'} max-w-[95vw]`} transition-all duration-200`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`bg-white dark:bg-gray-800 border-2 border-blue-500 shadow-2xl ring-4 ring-blue-500/20 ${isMaximized ? 'h-full rounded-none border-0 ring-0' : 'rounded-lg max-h-[90vh]'} flex flex-col`}>
          {/* Header */}
          <div className="px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-blue-500" />
                {t('admin.inlineEdit.editTranslation')}
              </h3>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleStyleAI}
                  disabled={aiStyleLoading}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-xs font-medium shadow-lg"
                  type="button"
                  title={t('admin.ai.styleWithAITitle') || 'Style or rewrite this content with AI'}
                >
                  {aiStyleLoading
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Sparkles className="w-3.5 h-3.5" />}
                  {t('admin.ai.styleWithAI') || 'Style with AI'}
                </button>
                <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={isHtmlMode}
                    onChange={(e) => setIsHtmlMode(e.target.checked)}
                    className="rounded"
                  />
                  {t('admin.common.htmlMode')}
                </label>
                <button
                  onClick={() => setIsMaximized(!isMaximized)}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-500 dark:text-gray-400"
                  title={isMaximized ? (t('admin.inlineEdit.restore') || 'Restore') : (t('admin.inlineEdit.maximize') || 'Maximize')}
                  type="button"
                >
                  {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {aiStyleOpen && (
              <div className="mt-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-300 dark:border-purple-700 rounded-lg space-y-2">
                <label className="block text-xs font-medium text-purple-900 dark:text-purple-200">
                  {t('admin.ai.promptLabel') || 'Describe the content you want'}
                </label>
                <textarea
                  value={aiStylePrompt}
                  onChange={(e) => setAiStylePrompt(e.target.value)}
                  placeholder={t('admin.ai.stylePromptPlaceholder')
                    || 'e.g. Rewrite as 2 short paragraphs with a bold lead line, or convert to a bulleted list, or generate a charity intro'}
                  disabled={aiStyleLoading}
                  rows={3}
                  className="w-full px-2 py-1.5 text-sm border border-purple-300 dark:border-purple-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-60"
                  autoFocus
                />
                {aiError && (
                  <div className="text-xs text-red-600 dark:text-red-400">{aiError}</div>
                )}
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => { setAiStyleOpen(false); setAiStylePrompt(''); setAiError(''); }}
                    disabled={aiStyleLoading}
                    type="button"
                    className="px-3 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-500 rounded-lg transition-colors disabled:opacity-60"
                  >
                    {t('admin.common.cancel')}
                  </button>
                  <button
                    onClick={handleStyleAISubmit}
                    disabled={aiStyleLoading || !aiStylePrompt.trim()}
                    type="button"
                    className="flex items-center gap-1 px-3 py-1 text-xs font-medium bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    {aiStyleLoading
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />{t('admin.ai.generating') || 'Generating…'}</>
                      : <><Sparkles className="w-3.5 h-3.5" />{t('admin.ai.generate') || 'Generate'}</>}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Body — scrollable */}
          <div className="overflow-y-auto flex-1 p-6">

            {/* Primary language */}
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">
                {langName(language) || language.toUpperCase()}
              </label>

              {/* HTML toolbar */}
              {isHtmlMode && (
                <div className="flex flex-wrap gap-1.5 mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
                  {[
                    { label: 'B', snippet: { open: '<strong>', close: '</strong>', placeholder: 'bold text' }, title: t('admin.inlineEdit.bold'), className: 'font-bold' },
                    { label: 'I', snippet: { open: '<em>', close: '</em>', placeholder: 'italic text' }, title: t('admin.inlineEdit.italic'), className: 'italic' },
                    { label: 'H2', snippet: { open: '<h2>', close: '</h2>', placeholder: 'Heading' }, title: t('admin.inlineEdit.h2') },
                    { label: 'H3', snippet: { open: '<h3>', close: '</h3>', placeholder: 'Heading' }, title: t('admin.inlineEdit.h3') },
                    { label: 'P', snippet: { open: '<p>', close: '</p>', placeholder: 'Paragraph text' }, title: t('admin.inlineEdit.p') },
                    { label: 'BR', snippet: { open: '<br />' }, title: t('admin.inlineEdit.br') },
                    { label: '🔗', snippet: { open: '<a href="">', close: '</a>', placeholder: 'link text' }, title: t('admin.inlineEdit.link') },
                    { label: t('admin.inlineEdit.span'), snippet: { open: '<span class="">', close: '</span>', placeholder: 'text' }, title: t('admin.inlineEdit.span') },
                  ].map(({ label: buttonLabel, snippet, title, className: cn }) => (
                    <button
                      key={title}
                      onClick={() => insertHtmlSnippet(snippet)}
                      className={`px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors ${cn || ''}`}
                      title={title}
                      type="button"
                    >
                      {buttonLabel}
                    </button>
                  ))}
                  <button
                    onClick={handleCleanHtml}
                    className="px-2 py-1 text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50 border border-amber-300 dark:border-amber-700 rounded transition-colors"
                    title={label('admin.inlineEdit.cleanHtmlTitle', 'Clean pasted HTML')}
                    type="button"
                  >
                    {label('admin.inlineEdit.cleanHtml', 'Clean HTML')}
                  </button>
                  {/* Image picker button */}
                  <button
                    onClick={openImagePicker}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-300 dark:border-blue-700 rounded transition-colors"
                    title={t('admin.inlineEdit.insertImageFromCdn') || 'Insert Image from CDN'}
                    type="button"
                  >
                    <Image className="w-3.5 h-3.5" />
                    {t('admin.inlineEdit.image') || 'Image'}
                  </button>
                  <button
                    onClick={openContentPicker}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 border border-emerald-300 dark:border-emerald-700 rounded transition-colors"
                    title={label('contentPicker.insertReferenceTitle', 'Insert live content reference')}
                    type="button"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    {label('contentPicker.toolbarButton', 'Content')}
                  </button>
                </div>
              )}

              {/* Token insertion strip */}
              {(() => {
                const TOKEN_GROUPS = [
                  {
                    label: 'Organization',
                    color: 'emerald',
                    tokens: [
                      { token: '{{organizationName}}', hint: 'Org display name' },
                      { token: '{{charityName}}', hint: 'Charity / beneficiary name' },
                      { token: '{{licenseNumber}}', hint: 'Gaming licence number' },
                    ],
                  },
                  {
                    label: 'Draw',
                    color: 'blue',
                    tokens: [
                      { token: '{{drawDate}}', hint: 'Next draw date' },
                      { token: '{{closeDate}}', hint: 'Ticket sales close date' },
                      { token: '{{jackpot}}', hint: 'Current jackpot amount' },
                      { token: '{{drawTitle}}', hint: 'Draw / game title' },
                      { token: '{{ticketPrice}}', hint: 'Ticket price' },
                    ],
                  },
                  {
                    label: 'Member',
                    color: 'violet',
                    tokens: [
                      { token: '{{firstName}}', hint: 'Member first name' },
                      { token: '{{lastName}}', hint: 'Member last name' },
                      { token: '{{email}}', hint: 'Member email' },
                    ],
                  },
                ];
                const borderMap = { emerald: 'border-emerald-300 dark:border-emerald-700', blue: 'border-blue-300 dark:border-blue-700', violet: 'border-violet-300 dark:border-violet-700' };
                const chipMap = { emerald: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 border-emerald-200 dark:border-emerald-700', blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/60 border-blue-200 dark:border-blue-700', violet: 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/60 border-violet-200 dark:border-violet-700' };
                return (
                  <div className="mb-2">
                    <button
                      type="button"
                      onClick={() => setShowTokens(v => !v)}
                      className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors mb-1"
                    >
                      <span className={`inline-block transition-transform duration-150 ${showTokens ? 'rotate-90' : ''}`}>▶</span>
                      Insert token
                    </button>
                    {showTokens && (
                      <div className="flex flex-col gap-1.5 p-2 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg">
                        {TOKEN_GROUPS.map(({ label: grpLabel, color, tokens }) => (
                          <div key={grpLabel} className={`flex items-start gap-0 pb-1.5 border-b last:border-b-0 ${borderMap[color]}`}>
                            <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 w-20 shrink-0 pt-0.5">{grpLabel}:</span>
                            <div className="flex flex-wrap gap-1.5">
                              {tokens.map(({ token, hint }) => (
                                <button
                                  key={token}
                                  type="button"
                                  onClick={() => insertToken(token)}
                                  title={hint}
                                  className={`px-2 py-0.5 text-xs font-mono rounded border transition-colors ${chipMap[color]}`}
                                >
                                  {token}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Split view: editor left, preview right */}
              <div className={isHtmlMode ? 'grid grid-cols-2 gap-4' : ''}>
                {/* Editor */}
                <div className="flex flex-col">
                  {isHtmlMode && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">
                      HTML
                    </div>
                  )}
                  {multiline || isHtmlMode ? (
                    <textarea
                      ref={textareaRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className={`w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-y text-base ${isHtmlMode ? 'font-mono text-sm flex-1 min-h-48' : 'resize-y'}`}
                      rows={isHtmlMode ? undefined : 4}
                    />
                  ) : (
                    <input
                      ref={inputRef}
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="w-full px-3 py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-base"
                    />
                  )}
                </div>

                {/* Live preview (HTML mode only) */}
                {isHtmlMode && (
                  <div className="flex flex-col">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 font-semibold uppercase tracking-wide">
                      {t('admin.common.preview')}
                    </div>
                    <div className="flex-1 p-3 bg-gray-50 dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-600 rounded-lg overflow-auto min-h-48">
                      {editValue ? (
                        <RichContentRenderer
                          html={previewHtml}
                          size="sm"
                          className="text-gray-900 dark:text-gray-100"
                        />
                      ) : (
                        <p className="text-xs text-gray-400 italic">{t('admin.inlineEdit.previewPlaceholder')}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Other languages */}
            {enabledLanguages.filter(lang => lang !== language).map((lang) => (
              <div key={lang} className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold text-purple-600 dark:text-purple-400">
                    {langName(lang)} {t('admin.inlineEdit.translation')}
                  </label>
                  <button
                    onClick={() => handleGenerateAI(lang)}
                    disabled={aiLoadingLang === lang}
                    className="flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-xs font-medium shadow-lg"
                    type="button"
                    title={t('admin.inlineEdit.translateWithAITitle') || 'Translate the primary-language value into this language'}
                  >
                    {aiLoadingLang === lang
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Sparkles className="w-3.5 h-3.5" />}
                    {t('admin.inlineEdit.translateWithAI')}
                  </button>
                </div>
                {aiError && aiLoadingLang === null && (
                  <div className="text-xs text-red-600 dark:text-red-400 mb-1">{aiError}</div>
                )}
                {multiline || isHtmlMode ? (
                  <textarea
                    value={otherLangValues[lang] || ''}
                    onChange={(e) => setOtherLangValues({...otherLangValues, [lang]: e.target.value})}
                    className={`w-full px-3 py-2.5 border-2 border-purple-300 dark:border-purple-600 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-y text-base ${isHtmlMode ? 'font-mono text-sm' : ''}`}
                    rows={isHtmlMode ? 4 : 4}
                    placeholder={t('admin.inlineEdit.enterTranslation')?.replace('{lang}', langName(lang)) || `Enter ${langName(lang)} translation...`}
                  />
                ) : (
                  <input
                    type="text"
                    value={otherLangValues[lang] || ''}
                    onChange={(e) => setOtherLangValues({...otherLangValues, [lang]: e.target.value})}
                    className="w-full px-3 py-2.5 border-2 border-purple-300 dark:border-purple-600 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 dark:focus:ring-purple-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-base"
                    placeholder={t('admin.inlineEdit.enterTranslation')?.replace('{lang}', langName(lang)) || `Enter ${langName(lang)} translation...`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="flex flex-col gap-1">
              {/* Tag row with copy button */}
              <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 font-mono">
                <span>{t('admin.inlineEdit.tag')}:</span>
                <span className="text-blue-600 dark:text-blue-400">{resolvedKey}</span>
                <button
                  type="button"
                  onClick={handleCopyTag}
                  className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={label('admin.inlineEdit.copyTag', 'Copy tag key')}
                >
                  {tagCopied
                    ? <CopyCheck className="w-3 h-3 text-green-500" />
                    : <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />}
                </button>
              </div>
              {/* Source indicator below tag */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500 dark:text-gray-400">Tag Type:</span>
                <Circle className={`w-2 h-2 fill-current ${source === 'organization' ? 'text-green-500' : source === 'translations' ? 'text-blue-500' : 'text-amber-500'}`} />
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {source === 'organization' ? (label('admin.inlineEdit.sourceOrganization', 'Organization')) : source === 'translations' ? (label('admin.inlineEdit.sourceDefault', 'Default')) : (label('admin.inlineEdit.sourceFallback', 'Fallback'))}
                </span>
                <span
                  className="flex-shrink-0 cursor-help"
                  title={source === 'organization'
                    ? 'Organization — this content is saved in the DB specifically for your organization and overrides the platform default.'
                    : source === 'translations'
                    ? 'Default — this content is saved in the DB as a platform-wide default. It is not org-specific and will be seen by all orgs unless overridden.'
                    : 'Fallback — no DB content is saved for this key yet. The displayed text comes from the built-in code default.'}
                >
                  <Info className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="flex items-center gap-1.5 px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition-colors shadow-sm hover:shadow"
                title={t('admin.common.cancel')}
              >
                <X className="w-4 h-4" />
                {t('admin.common.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors shadow-sm hover:shadow"
                title={t('admin.common.save')}
              >
                <Check className="w-4 h-4" />
                {t('admin.common.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      {showImagePicker && (
        <ImagePickerModal
          onSelect={handleImageSelect}
          onClose={() => setShowImagePicker(false)}
        />
      )}
      {showContentPicker && (
        <ContentPickerModal
          currentKey={resolvedKey}
          onSelect={handleContentSelect}
          onClose={() => setShowContentPicker(false)}
        />
      )}
    </>
  );
}
